import React from 'react';
import { Box } from '@mui/material';
import { ShareButton } from './ShareButton/ShareButton';
import {
  trackInteractionEvent,
  useAuthenticatedUser,
  useFocusingAdviserProfile,
} from 'hooks/hooks';
import { api } from 'services/api';
import { useAdvisorProfileTheme } from '../AdvisorProfile/AdvisorProfileThemeProvider';
import { useTranslation } from 'react-i18next';
import { Share, WhatsApp } from '@mui/icons-material';
import { isMobileBrowser } from 'utils/DeviceInfo/deviceOS';
import { isFrameCheck, postMessageToParent } from 'utils/Frame/Frame';

export function ShareButtonBar({
  message,
  adviserLink,
}: {
  message: string;
  adviserLink: string;
}) {
  const AuthenticatedUser = useAuthenticatedUser();
  const FocusingAuthenticatedUser = useFocusingAdviserProfile();
  const [trackDirect] = api.useTrackDirectMutation();
  const { buttonSx } = useAdvisorProfileTheme();
  const { t } = useTranslation();
  const whatsAppSendHref = `https://api.whatsapp.com/send?text=${encodeURIComponent(
    message,
  )}`;
  const isMobileOS = isMobileBrowser();
  const isFrame = isFrameCheck();

  if (!FocusingAuthenticatedUser.advisor?.adviserInfo._id) return <></>;

  async function nativeShare() {
    handleTrackShareButton();
    if (isFrame) {
      // If its a frame, we need to send the message to the parent window since the native share is not supported in the iframe
      postMessageToParent({
        success: true,
        code: 'SHARE',
        message: 'SHARE',
        data: { text: message },
      });
    }
  }

  const handleTrackShareButton = () => {
    const focusingAdviser = FocusingAuthenticatedUser.advisor!.adviserInfo._id;
    if (AuthenticatedUser.isAdviserProfileOwner(focusingAdviser)) {
      trackDirect({ type: 'adviserShareEcard' });
    }
    trackInteractionEvent({
      type: 'Content Copied',
      event_properties: {
        target: focusingAdviser,
        'Content Name': 'Adviser Share Message',
        Name: 'Adviser Share Modal',
        Component: 'Adviser Share Modal',
        'Event Name': 'Adviser share E-Name card',
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}>
      <ShareButton
        sx={buttonSx}
        label={t('Global.Send')}
        icon={<WhatsApp />}
        onShare={(e: React.MouseEvent) => {
          e.preventDefault();
          handleTrackShareButton();
          window.open(whatsAppSendHref, '_blank');
        }}
      />
      {isMobileOS && isFrame && (
        <ShareButton
          sx={buttonSx}
          label={t('Global.Share')}
          icon={<Share />}
          onShare={nativeShare}
        />
      )}
    </Box>
  );
}
